export type CaptionSize = { preferred: number; word: number; wholeWord?: number; center: number };

/** Allocate a two-column row without overlapping captions or leaving the chapter. */
export function paletteCaptionLayout(width: number, captions: CaptionSize[], gap = 8) {
  if (!captions.length) return [];
  const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));
  if (captions.length === 1) {
    const size = Math.min(width, captions[0].preferred);
    return [{ width: size, left: clamp(captions[0].center - size / 2, 0, width - size),
      hyphenate: (captions[0].wholeWord ?? captions[0].word) > size }];
  }
  const available = Math.max(0, width - gap);
  const [a, b] = captions;
  let left = available / 2;
  if (a.preferred < left && b.preferred > left) left = Math.max(a.preferred, available - b.preferred);
  else if (b.preferred < left && a.preferred > left) left = Math.min(a.preferred, available - b.preferred);
  const minA = Math.min(a.word, available);
  const minB = Math.min(b.word, available);
  if (minA + minB <= available) left = clamp(left, minA, available - minB);
  const wholeA = a.wholeWord ?? a.word;
  const wholeB = b.wholeWord ?? b.word;
  // Prefer allocating enough space for whole words before falling back to syllables.
  if (wholeA + wholeB <= available) left = clamp(left, wholeA, available - wholeB);
  return captions.map((caption, index) => {
    const start = index === 0 ? 0 : left + gap;
    const capacity = index === 0 ? left : available - left;
    const size = Math.min(caption.preferred, capacity);
    return { width: size, left: clamp(caption.center - size / 2, start, start + capacity - size),
      hyphenate: (caption.wholeWord ?? caption.word) > size };
  });
}
